import { scaleLinear, scaleTime } from 'd3-scale';
import { area, line } from 'd3-shape';
import { useId, useMemo, useRef, type KeyboardEvent, type PointerEvent } from 'react';
import { cn } from '../../../lib/cn';
import { nearestIndex, type ChartMarker, type ChartPoint, type ChartThreshold } from '../../../lib/metrics';
import { axisTimeFormatter } from '../../../lib/time';
import { ChartTooltip } from '../../molecules';
import { useChartSync } from './chartSync';
import { useElementWidth } from './useElementWidth';

interface LineChartProps {
  points: ChartPoint[];
  fromMs: number;
  toMs: number;
  binMs: number;
  pollIntervalMs: number;
  formatValue: (value: number) => string;
  formatTick: (value: number) => string;
  /** Fixed y-domain (e.g. 0–100 for percentages); otherwise fitted to data + threshold. */
  domain?: [number, number];
  threshold?: ChartThreshold;
  /** Alerts sent for this metric, drawn as ticks on the time axis. */
  markers?: ChartMarker[];
  ariaLabel: string;
  /** Total height including the x-axis band. */
  height?: number;
}

/** End dot / cursor marker: series fill with a 2px surface ring. */
const MARKER = 'fill-series-1 stroke-surface stroke-2';

const DEFAULT_HEIGHT = 200; // includes the x-axis band
const MARGIN = { top: 12, right: 12, bottom: 26, left: 48 };

const MARKER_STROKE = { critical: 'stroke-critical', warning: 'stroke-warning', neutral: 'stroke-neutral', good: 'stroke-good' } as const;
const MARKER_FILL = { critical: 'fill-critical', warning: 'fill-warning', neutral: 'fill-neutral', good: 'fill-good' } as const;

export function LineChart(props: LineChartProps) {
  const { points, fromMs, toMs, binMs, pollIntervalMs, formatValue, formatTick, domain, threshold, markers = [], ariaLabel } = props;
  const height = props.height ?? DEFAULT_HEIGHT;
  const containerRef = useRef<HTMLDivElement>(null);
  const width = useElementWidth(containerRef);
  const chartId = useId();
  const fadeId = `${chartId}-fade`;
  const sync = useChartSync();
  const active = sync.t !== null ? nearestIndex(points, sync.t) : null;

  const plotRight = Math.max(MARGIN.left + 10, width - MARGIN.right);
  const plotBottom = height - MARGIN.bottom;
  const showBand = binMs > pollIntervalMs;

  const x = useMemo(() => scaleTime().domain([fromMs, toMs]).range([MARGIN.left, plotRight]), [fromMs, toMs, plotRight]);

  const y = useMemo(() => {
    let [lo, hi] = domain ?? fitDomain(points, showBand, threshold?.value);
    if (!domain) {
      const pad = (hi - lo) * 0.12 || Math.abs(hi) * 0.1 || 1;
      lo = lo >= 0 ? Math.max(0, lo - pad) : lo - pad;
      hi += pad;
    }
    return scaleLinear().domain([lo, hi]).range([plotBottom, MARGIN.top]).nice(4);
  }, [domain, points, showBand, threshold?.value, plotBottom]);

  /** Nulls break the line where polls are missing, so gaps read as gaps. */
  const withGaps = useMemo(() => {
    const out: (ChartPoint | null)[] = [];
    points.forEach((p, i) => {
      const prev = points[i - 1];
      if (prev && p.t - prev.t > binMs * 1.5) out.push(null);
      out.push(p);
    });
    return out;
  }, [points, binMs]);

  const paths = useMemo(() => {
    const defined = (p: ChartPoint | null): p is ChartPoint => p !== null;
    return {
      fill:
        area<ChartPoint | null>().defined(defined).x((p) => x(p!.t)).y0(plotBottom).y1((p) => y(p!.avg))(withGaps) ?? '',
      line: line<ChartPoint | null>().defined(defined).x((p) => x(p!.t)).y((p) => y(p!.avg))(withGaps) ?? '',
      band:
        area<ChartPoint | null>().defined(defined).x((p) => x(p!.t)).y0((p) => y(p!.min)).y1((p) => y(p!.max))(withGaps) ?? '',
      isolated: withGaps.filter((p, i): p is ChartPoint => p !== null && !withGaps[i - 1] && !withGaps[i + 1]),
    };
  }, [withGaps, x, y, plotBottom]);

  const tickLabel = axisTimeFormatter(toMs - fromMs);
  // Day-level labels on 12-hour ticks would repeat ("Tue 22, Tue 22"); keep the first of each.
  const xTicks = x
    .ticks(Math.max(2, Math.floor((plotRight - MARGIN.left) / 110)))
    .filter((tick, i, all) => i === 0 || tickLabel(tick) !== tickLabel(all[i - 1]!));
  const yTicks = y.ticks(height < 180 ? 2 : 4);
  const [yMin, yMax] = y.domain() as [number, number];
  const thresholdVisible = threshold && threshold.value >= yMin && threshold.value <= yMax;
  const visibleMarkers = markers.filter((m) => m.t >= fromMs && m.t <= toMs);

  const activePoint = active !== null ? points[active] : undefined;
  const lastPoint = points[points.length - 1];

  function setActive(index: number | null) {
    sync.set(index === null ? null : points[index]!.t, index === null ? null : chartId);
  }

  function onPointerMove(event: PointerEvent) {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setActive(nearestIndex(points, x.invert(event.clientX - rect.left).getTime()));
  }

  function onKeyDown(event: KeyboardEvent) {
    if (!points.length) return;
    const last = points.length - 1;
    const moves: Record<string, () => number> = {
      ArrowLeft: () => Math.max(0, (active ?? last + 1) - 1),
      ArrowRight: () => Math.min(last, (active ?? -1) + 1),
      Home: () => 0,
      End: () => last,
    };
    if (event.key === 'Escape') return setActive(null);
    const move = moves[event.key];
    if (move) {
      event.preventDefault();
      setActive(move());
    }
  }

  return (
    <div
      ref={containerRef}
      className="relative w-full touch-pan-y rounded-sm focus-visible:outline-offset-4"
      style={{ height }}
      tabIndex={0}
      role="img"
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
      onBlur={() => sync.source === chartId && setActive(null)}
    >
      {width > 0 && (
        <svg width={width} height={height} aria-hidden className="block overflow-visible font-mono text-[10.5px] [&_text]:fill-fg-muted">
          <defs>
            <linearGradient id={fadeId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="var(--series-1-fade)" />
              <stop offset="1" stopColor="var(--series-1-fade)" stopOpacity={0} />
            </linearGradient>
          </defs>

          <g className="stroke-grid [shape-rendering:crispEdges]">
            {yTicks.map((tick) => (
              <g key={tick}>
                <line x1={MARGIN.left} x2={plotRight} y1={y(tick)} y2={y(tick)} />
                <text x={MARGIN.left - 8} y={y(tick)} dy="0.32em" textAnchor="end" className="stroke-none">
                  {formatTick(tick)}
                </text>
              </g>
            ))}
          </g>

          <line className="stroke-axis [shape-rendering:crispEdges]" x1={MARGIN.left} x2={plotRight} y1={plotBottom} y2={plotBottom} />
          <g>
            {xTicks.map((tick) => (
              <text key={tick.getTime()} x={x(tick)} y={plotBottom + 17} textAnchor="middle">
                {tickLabel(tick)}
              </text>
            ))}
          </g>

          {visibleMarkers.map((m) => (
            <g key={`${m.t}-${m.label}`}>
              <line
                x1={x(m.t)}
                x2={x(m.t)}
                y1={MARGIN.top}
                y2={plotBottom}
                className={cn('[stroke-dasharray:2_3] [shape-rendering:crispEdges]', MARKER_STROKE[m.tone])}
              />
              <path d={`M${x(m.t) - 4},${plotBottom} l4,-6 l4,6 z`} className={MARKER_FILL[m.tone]}>
                <title>{m.label}</title>
              </path>
            </g>
          ))}

          {showBand ? <path className="fill-series-1-wash" d={paths.band} /> : <path fill={`url(#${fadeId})`} d={paths.fill} />}
          <path className="fill-none stroke-series-1 stroke-2 [stroke-linecap:round] [stroke-linejoin:round]" d={paths.line} />
          {paths.isolated.map((p) => (
            <circle key={p.t} className="fill-series-1" cx={x(p.t)} cy={y(p.avg)} r={2.5} />
          ))}

          {thresholdVisible && (
            <g>
              <line
                x1={MARGIN.left}
                x2={plotRight}
                y1={y(threshold.value)}
                y2={y(threshold.value)}
                className={cn(
                  '[stroke-dasharray:4_3] [shape-rendering:crispEdges]',
                  threshold.tone === 'critical' ? 'stroke-critical' : 'stroke-serious',
                )}
              />
              <text
                x={MARGIN.left + 6}
                y={y(threshold.value) - 5}
                textAnchor="start"
                className={cn(
                  'font-sans text-[11px] font-medium [paint-order:stroke] stroke-surface [stroke-linejoin:round] [stroke-width:4px]',
                  threshold.tone === 'critical' ? 'fill-chip-critical-fg!' : 'fill-chip-warning-fg!',
                )}
              >
                {threshold.label}
              </text>
            </g>
          )}

          {activePoint ? (
            <g>
              <line x1={x(activePoint.t)} x2={x(activePoint.t)} y1={MARGIN.top} y2={plotBottom} className="stroke-fg-muted [shape-rendering:crispEdges]" />
              <circle cx={x(activePoint.t)} cy={y(activePoint.avg)} r={4} className={MARKER} />
            </g>
          ) : (
            lastPoint && <circle className={MARKER} cx={x(lastPoint.t)} cy={y(lastPoint.avg)} r={3.5} />
          )}

          <rect
            className="cursor-crosshair fill-transparent"
            x={MARGIN.left}
            y={0}
            width={Math.max(0, plotRight - MARGIN.left)}
            height={plotBottom}
            onPointerMove={onPointerMove}
            onPointerLeave={() => setActive(null)}
          />
        </svg>
      )}

      {activePoint && sync.source === chartId && (
        <ChartTooltip
          point={activePoint}
          left={x(activePoint.t)}
          width={width}
          binMs={binMs}
          showRange={showBand}
          formatValue={formatValue}
        />
      )}
    </div>
  );
}

function fitDomain(points: ChartPoint[], useBand: boolean, threshold?: number): [number, number] {
  let lo = Infinity;
  let hi = -Infinity;
  for (const p of points) {
    lo = Math.min(lo, useBand ? p.min : p.avg);
    hi = Math.max(hi, useBand ? p.max : p.avg);
  }
  if (threshold !== undefined) {
    lo = Math.min(lo, threshold);
    hi = Math.max(hi, threshold);
  }
  return Number.isFinite(lo) ? [lo, hi] : [0, 1];
}
